import { useKlusSelectedKlusStore } from "@personeel/stores/SelectedKlusStore.ts";
import { dateTimeToMinute } from "@shared/common/utils/luxonHelpers";
import { DateTime } from "luxon";
import { useCallback, useLayoutEffect, useRef } from "react";
import { KlusModel } from "@personeel//models/KlusModel";
import { calcPosition, calcWidth } from "@personeel/utils/TijdlijnDateCalcUtils";
import styles from "./Klus.module.css";
import { Periode, useKlusDrag } from "@personeel/components/Werklijnen/hooks/useKlusDrag";

interface IKlus {
  timeLineBegin: DateTime;
  timeLineEnd: DateTime;
  scale: number;
  klus: KlusModel;
}

function getKlusColor(klus: KlusModel) {
  return klus.bewegingsCode
    ? styles["klus-line-color-bewegingsgebonden"]
    : styles["klus-line-color-activiteitgebonden"];
}

function isKlusHasInner(klus: KlusModel) {
  return ["SCHAFT", "SPLITS", "COMB", "ASCOMB", "GMC", "VGMC"].includes(klus.klusSoort);
}

function getInnerLineStyle(klus: KlusModel) {
  if (["LOPEN"].includes(klus.klusSoort)) return styles["klus-line-color-verplaatsing-inner"];
  if (isKlusHasInner(klus) || klus.bewegingsCode) return styles["klus-line-secondary"];
}

export function Klus({ timeLineBegin, timeLineEnd, scale, klus }: Readonly<IKlus>) {
  // baseLeft: absolute pixel-positie van de klus op de tijdlijn op basis van beginDatumtijd
  const baseLeft = calcPosition(klus.beginDatumtijd, timeLineBegin, timeLineEnd, scale);
  const width = calcWidth(klus.beginDatumtijd, klus.eindDatumtijd, timeLineEnd, scale);
  const selectedKlus = useKlusSelectedKlusStore((s) => s.selectedKlus);

  // Ref op de button: positie wordt tijdens drag direct via DOM gezet (geen re-render per frame)
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Ref op het tijdelement: tijden worden tijdens drag direct via DOM bijgewerkt (geen re-render per frame)
  const tijdRef = useRef<HTMLDivElement>(null);

  // Initiële tijdweergave (voor render en na drop)
  // Indien er met pinttijden gewerkt moet worden kan dateTimeToMinute vervangen worden door dateTimeToMinuteWithPint
  const beginTijd = dateTimeToMinute(klus.beginDatumtijd);
  const eindTijd = dateTimeToMinute(klus.eindDatumtijd);

  const spoorStart = `${klus.dienstregelpuntStart}-${klus.spoorStart}`;
  const spoorEind = `${klus.dienstregelpuntEind}-${klus.spoorEind}`;
  const spoorTekst = spoorStart === spoorEind ? spoorStart : `${spoorStart} ${spoorEind}`;

  // Callback voor directe DOM-update van positie tijdens drag
  const onDragOffsetChange = useCallback(
    (offsetPx: number) => {
      if (buttonRef.current) {
        buttonRef.current.style.left = `${baseLeft + offsetPx}px`;
      }
    },
    [baseLeft],
  );

  // Callback voor directe DOM-update van tijdweergave tijdens drag
  const onDragMinutesChange = useCallback(
    (minutes: number) => {
      if (tijdRef.current) {
        const begin = dateTimeToMinute(klus.beginDatumtijd.plus({ minutes }));
        const eind = dateTimeToMinute(klus.eindDatumtijd.plus({ minutes }));
        tijdRef.current.textContent = `${begin}–${eind}`;
      }
    },
    [klus.beginDatumtijd, klus.eindDatumtijd],
  );

  // Callback voor directe DOM-update met de definitieve positie na drop — vermijdt React useEffect
  // o.a. om bij korte sleepacties van klus het even verspringen van de klus tegen te gaan
  const onFinalOffsetSet = useCallback(
    (offsetPx: number) => {
      if (buttonRef.current) {
        buttonRef.current.style.left = `${baseLeft + offsetPx}px`;
      }
    },
    [baseLeft],
  );

  // Zet de initiële positie en herstel na store-update (klus.beginDatumtijd gewijzigd na drop).
  // useLayoutEffect vuurt synchroon vóór de browser paint — geen flicker.
  // Na een drop zet onFinalOffsetSet al de juiste positie; deze effect zet hem terug naar 0-offset
  // zodra de store de nieuwe beginDatumtijd heeft doorgegeven.
  // o.a. om bij korte sleepacties van klus het even verspringen van de klus tegen te gaan
  useLayoutEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.style.left = `${baseLeft}px`;
    }
  }, [baseLeft]);

  // Reset tijdweergave naar actuele klus-tijden na store-update (bijv. na drag of na aanpassing in sidepanel).
  // tijdRef.textContent kan handmatig overschreven zijn tijdens drag; dit herstelt de correcte waarde.
  useLayoutEffect(() => {
    if (tijdRef.current) {
      tijdRef.current.textContent = `${beginTijd}–${eindTijd}`;
    }
  }, [beginTijd, eindTijd]);

  // Alle drag-logica zit in de custom hook useKlusDrag
  const timeLinePeriode: Periode = { begin: timeLineBegin, eind: timeLineEnd };
  const { isDragging, onPointerDown } = useKlusDrag({
    klus,
    timeLinePeriode,
    scale,
    // Directe DOM-updates tijdens drag: geen React state → geen gebatchte updates die na drop nog binnenkomen
    onDragOffsetChange,
    onDragMinutesChange,
    onFinalOffsetSet,
  });

  return (
    <button
      type="button"
      ref={buttonRef}
      id={"ScrollableTypes.Klus" + klus.id}
      data-testid={`werklijn-klus-button-${klus.id}`}
      style={{ width, cursor: isDragging ? "grabbing" : "grab" }}
      className={`${styles["klus-wrapper"]} ${isDragging || selectedKlus?.id === klus.id ? styles["klus-dragging"] : ""}`}
      onPointerDown={onPointerDown}
    >
      <div className={styles["klus-codes-werkzaamheid"]}>{klus.klusSoort}</div>
      <div ref={tijdRef} className={styles["klus-tijd"]}>
        {beginTijd}–{eindTijd}
      </div>
      <div
        data-testid={`werklijn-klus-balk-${klus.id}`}
        style={{ width }}
        className={styles["klus-line"] + " " + getKlusColor(klus)}
      >
        {isKlusHasInner(klus) && <div className={getInnerLineStyle(klus)} />}
      </div>
      <div className={styles["klus-spoor"]}>{spoorTekst}</div>
      <div className={styles["klus-codes-beweging"]}>{klus.bewegingsCode}</div>
    </button>
  );
}
